import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Lock, Eye, EyeOff, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { getTenantId } from '../../config/tenantConfig';
import { getValidAuthSession } from '../../utils/authStorage';
import './index.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleClose = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleForgotPassword = () => {
    // Reuses the existing /forgot-password page's own Email -> Code ->
    // Reset flow — no separate reset logic here, just closing this modal
    // (same cleanup as any other close) before navigating away.
    handleClose();
    navigate('/forgot-password');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    const token = getValidAuthSession()?.token;
    if (!token) {
      setError('Session expired. Please login again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.put(`${API_BASE_URL}/profile/change-password`, {
        old_password: oldPassword,
        password: newPassword,
        password_confirmation: confirmPassword
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': X_API_KEY,
          'X-Tenant': getTenantId(),
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.errors?.old_password) {
        setError(errData.errors.old_password[0]);
      } else if (errData?.message) {
        setError(errData.message);
      } else {
        setError('Failed to change password. Please check your current password.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-pw-overlay" onClick={handleClose}>
      <div className="change-pw-modal" onClick={(e) => e.stopPropagation()}>
        <button className="change-pw-close" onClick={handleClose}>
          <X size={24} />
        </button>

        <div className="change-pw-content">
          <div className="change-pw-header">
            <div className="change-pw-icon">
              <Lock size={24} />
            </div>
            <h2 className="change-pw-title">Change Password</h2>
            <p className="change-pw-subtitle">Update your account security</p>
          </div>

          {success ? (
            <div className="change-pw-success-view">
              <CheckCircle size={48} className="change-pw-success-icon" />
              <p>Password changed successfully!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="change-pw-form">
              <div className="change-pw-field">
                <label>Current Password</label>
                <div className="change-pw-input-wrapper">
                  <input
                    type={showOldPw ? 'text' : 'password'}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="Enter current password"
                    required
                  />
                  <button type="button" onClick={() => setShowOldPw(!showOldPw)}>
                    {showOldPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <button type="button" className="change-pw-forgot-link" onClick={handleForgotPassword}>
                  Forgot Password?
                </button>
              </div>

              <div className="change-pw-field">
                <label>New Password</label>
                <div className="change-pw-input-wrapper">
                  <input
                    type={showNewPw ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    required
                  />
                  <button type="button" onClick={() => setShowNewPw(!showNewPw)}>
                    {showNewPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="change-pw-field">
                <label>Confirm New Password</label>
                <input
                  type={showNewPw ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                />
              </div>

              {error && (
                <div className="change-pw-error">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <button type="submit" className="change-pw-submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader size={18} className="change-pw-spin" />
                    <span>Updating...</span>
                  </>
                ) : 'Update Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
